// Room photos manager — upload, reorder, set cover and delete images for one room type.
import React, { useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Loader2, Star, Trash2, Upload, X } from 'lucide-react';
import { API_URL, apiCall, getToken, readCookie } from './_shared';

const RoomPhotosManager = ({ room, onClose, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState([]);
  const inputRef = useRef(null);
  const images = room.images || [];

  const reorder = async (urls) => {
    setBusy(true);
    try {
      await apiCall(`/api/admin/rooms/${room.id}/images`, { method: 'POST', body: JSON.stringify({ action: 'reorder', urls }) });
      await onChanged();
    } catch (e) { setErrors([e.message]); }
    finally { setBusy(false); }
  };

  const remove = async (url) => {
    if (!window.confirm('Remove this photo from the room?')) return;
    setBusy(true);
    try {
      await apiCall(`/api/admin/rooms/${room.id}/images`, { method: 'POST', body: JSON.stringify({ action: 'remove', url }) });
      await onChanged();
    } catch (e) { setErrors([e.message]); }
    finally { setBusy(false); }
  };

  const move = (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    reorder(next);
  };

  const setCover = (i) => {
    const next = [...images];
    const [pick] = next.splice(i, 1);
    reorder([pick, ...next]);
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    setErrors([]);
    const errs = [];
    const token = getToken();
    const csrf = readCookie('csrf_token');
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress({ done: i, total: files.length, name: f.name });
      const fd = new FormData();
      fd.append('file', f);
      try {
        const resp = await fetch(`${API_URL}/api/admin/rooms/${room.id}/upload-image`, {
          method: 'POST',
          credentials: 'include',
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(csrf ? { 'X-CSRF-Token': csrf } : {}) },
          body: fd,
        });
        if (!resp.ok) {
          const data = await resp.json().catch(() => ({}));
          errs.push(`${f.name}: ${data.detail || 'upload failed'}`);
        }
      } catch (e) {
        errs.push(`${f.name}: ${e.message}`);
      }
    }
    setProgress(null);
    setErrors(errs);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    await onChanged();
  };

  const onDrop = (e) => {
    e.preventDefault();
    upload(Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/')));
  };

  const tiles = images.map((url, i) => (
    <div key={url} className="relative group rounded-lg overflow-hidden border bg-slate-50" data-testid={`room-photo-${i}`}>
      <img src={url} alt="" className="w-full h-32 object-cover" />
      {i === 0 && (
        <span className="absolute top-1 left-1 text-[10px] font-bold uppercase tracking-wide bg-orange-500 text-white px-1.5 py-0.5 rounded">Cover</span>
      )}
      <div className="absolute inset-x-0 bottom-0 flex justify-between items-center gap-1 p-1 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => move(i, -1)} disabled={busy || i === 0} className="p-1 rounded bg-white/90 hover:bg-white disabled:opacity-30" title="Move left" data-testid={`room-photo-left-${i}`}><ArrowLeft className="w-3.5 h-3.5" /></button>
        <button onClick={() => setCover(i)} disabled={busy || i === 0} className="p-1 rounded bg-white/90 hover:bg-white disabled:opacity-30" title="Set as cover" data-testid={`room-photo-cover-${i}`}><Star className="w-3.5 h-3.5 text-orange-500" /></button>
        <button onClick={() => remove(url)} disabled={busy} className="p-1 rounded bg-white/90 hover:bg-red-50" title="Remove" data-testid={`room-photo-delete-${i}`}><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
        <button onClick={() => move(i, 1)} disabled={busy || i === images.length - 1} className="p-1 rounded bg-white/90 hover:bg-white disabled:opacity-30" title="Move right" data-testid={`room-photo-right-${i}`}><ArrowRight className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  ));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="room-photos-manager">
        <div className="flex items-start justify-between p-5 border-b">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Photos · {room.name}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{images.length} photo{images.length === 1 ? '' : 's'} · the first one is the cover shown on the website and booking pages.</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="room-photos-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-orange-300 bg-orange-50/50 rounded-xl p-6 text-center cursor-pointer hover:bg-orange-50"
            data-testid="room-photos-dropzone"
          >
            {busy && progress ? (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-700">
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading {progress.done + 1}/{progress.total} · {progress.name}
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-700">
                <Upload className="w-4 h-4 text-orange-600" /> Drop photos here or <span className="font-semibold text-orange-600">choose files</span> (JPG/PNG/WEBP, up to 10 MB each)
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(e) => upload(Array.from(e.target.files || []))}
              data-testid="room-photos-input"
            />
          </div>

          {images.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No photos yet — upload the first one above.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="room-photos-grid">{tiles}</div>
          )}
          {errors.map((e, i) => <p key={i} className="text-xs text-red-600" data-testid="room-photos-error">{e}</p>)}
        </div>
      </div>
    </div>
  );
};

export default RoomPhotosManager;
